import { useEffect, useRef, type ReactNode } from "react";

/**
 * A self-contained animated particle network for the login FORM panel background (echoing the
 * connected-dots look of neuronest.cc/admin). Pure `<canvas>` + requestAnimationFrame — NO external
 * scripts or assets, so it stays inside the BFF's strict CSP. Decorative only (aria-hidden), it
 * respects `prefers-reduced-motion` by rendering a single static frame, and tears the loop down on
 * unmount. Colors are read from the current CSS custom properties so it tracks the navy/blue theme.
 */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export function ParticleField(): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;

    const parent = canvas.parentElement ?? canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const styles = getComputedStyle(canvas);
    const dot = styles.getPropertyValue("--accent").trim() || "#5f8aa5";
    const line = styles.getPropertyValue("--accent-3").trim() || "#7aa7c4";

    let particles: Particle[] = [];
    let w = 0;
    let h = 0;

    const resize = (): void => {
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(26, Math.min(70, Math.round((w * h) / 14000)));
      particles = Array.from({ length: count }, (): Particle => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
      }));
    };

    const LINK = 130;
    const draw = (): void => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
      }
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        if (a === undefined) continue;
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          if (b === undefined) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK) {
            ctx.strokeStyle = line;
            ctx.globalAlpha = (1 - d / LINK) * 0.22;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = dot;
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    let raf = 0;
    const loop = (): void => {
      draw();
      raf = window.requestAnimationFrame(loop);
    };

    resize();
    if (reduced) {
      draw();
    } else {
      loop();
    }
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (raf !== 0) window.cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="oa-login__particles" aria-hidden="true" />;
}
